import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudentHome.css';
import CheckIn from '../CheckIn/CheckIn';
import { FaHome, FaChartLine, FaUser } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { useStudentAuth } from '../../lib/AuthProvider';
import { getSupabase } from '../../lib/supabaseClient';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StudentHome: React.FC = () => {
  const navigate = useNavigate();
  const { student, loading: authLoading, signOut } = useStudentAuth();
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Check session validity on component mount and periodically
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in StudentHome, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        // If session exists but no student data and not loading, wait a bit more
        if (!student && !authLoading) {
          console.log('No student data but session exists, waiting for AuthProvider to load...');
          // Give AuthProvider more time to load the profile
          setTimeout(() => {
            if (!student && !authLoading) {
              console.log('Still no student data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in StudentHome:', error);
      }
    };

    // Only check session if we're not loading
    if (!authLoading) {
      checkSession();
    }
    
    // Check session periodically
    const intervalId = setInterval(checkSession, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, [student, authLoading, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const dailyTimeRecords = [
    { day: 'Monday', hours: 7 },
    { day: 'Tuesday', hours: 3 },
    { day: 'Wednesday', hours: 5 },
    { day: 'Thursday', hours: 7 },
    { day: 'Friday', hours: 4 },
    { day: 'Saturday', hours: 6 }
  ];

  return (
    <div className="student-home">
      <header className="top-nav">
        <div className="logo">
          <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
        </div>
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          aria-controls="mobile-sidebar"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          {/* Unicode hamburger icon */}
          {'\u2630'}
        </button>
        <div className="nav-links">
          <a href="#" className="nav-link active"><FaHome className="nav-icon" /> Home</a>
          <a href="#" className="nav-link"><FaChartLine className="nav-icon" /> Progress</a>
          <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <a href="#" className="nav-link"><FaHome className="nav-icon" /> Home</a>
          <a href="#" className="nav-link"><FaChartLine className="nav-icon" /> Progress</a>
          <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Welcome,<br/>
            {authLoading ? "Loading..." : 
              student?.last_name && student?.first_name ? 
                `${student.last_name}, ${student.first_name}` :
                "Student"
            }
          </h1>
          <p>How are we feeling today?</p>
        </div>

        <div className="dashboard-grid">
          <div className="panel actions-panel">
            <div className="panel-header">Actions</div>
            <div className="panel-content">
              <button className="action-button" onClick={() => setShowCheckIn(true)}>Time In / Time Out</button>
              <button className="action-button">Classwork</button>
            </div>
          </div>

          <div className="panel time-records-panel">
            <div className="panel-header">Daily Time Records</div>
            <div className="panel-content">
              <Bar
                data={{
                  labels: dailyTimeRecords.map(record => record.day),
                  datasets: [
                    {
                      label: 'Hours',
                      data: dailyTimeRecords.map(record => record.hours),
                      backgroundColor: '#0044cc',
                      borderRadius: 4,
                      maxBarThickness: 30
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 8,
                      ticks: {
                        stepSize: 1,
                        callback: (value: number | string) => `${value} hours`
                      },
                      grid: {
                        color: '#e0e0e0'
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: (tooltipItem) => `${tooltipItem.raw} hours`
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </main>
      {showCheckIn && <CheckIn onClose={() => setShowCheckIn(false)} />}
    </div>
  );
};

export default StudentHome;
